import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMsal } from "@azure/msal-react";
import { API_URL, CLIENT_ID, AUTHORITY, REDIRECT_URI, GRAPH_SCOPES, GRAPH_API_ENDPOINT } from '../config/api';

// MSAL Configuration
export const msalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: AUTHORITY,
    redirectUri: REDIRECT_URI,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

export const loginRequest = {
  scopes: GRAPH_SCOPES,
};

// Microsoft Graph API utility function
const fetchUserDepartment = async (instance) => {
  try {
    const account = instance.getActiveAccount();
    if (!account) {
      throw new Error("No active account found");
    }

    // Acquire token silently for Microsoft Graph API
    const response = await instance.acquireTokenSilent({
      scopes: GRAPH_SCOPES,
      account: account
    });

    const graphResponse = await fetch(`${GRAPH_API_ENDPOINT}/me?$select=id,displayName,mail,department,jobTitle,officeLocation`, {
      headers: {
        'Authorization': `Bearer ${response.accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!graphResponse.ok) {
      throw new Error(`Graph API error: ${graphResponse.status}`);
    }

    const userData = await graphResponse.json();
    return {
      department: userData.department || 'General',
      jobTitle: userData.jobTitle || '',
      officeLocation: userData.officeLocation || ''
    };
  } catch (error) {
    console.error("Error fetching user department:", error);
    return {
      department: 'General',
      jobTitle: '',
      officeLocation: ''
    };
  }
};

// Token management utilities
const TOKEN_KEY = 'eshp_auth_token';

const tokenManager = {
  // Store token in localStorage
  setToken: (token) => {
    localStorage.setItem(TOKEN_KEY, token);
  },

  // Get token from localStorage
  getToken: () => {
    return localStorage.getItem(TOKEN_KEY);
  },

  // Remove token from localStorage
  removeToken: () => {
    localStorage.removeItem(TOKEN_KEY);
  },

  // Clear all auth data
  clearAuth: () => {
    localStorage.removeItem(TOKEN_KEY);
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem(TOKEN_KEY);
  },

  // Get authorization header for API requests
  getAuthHeader: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
};

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const { instance } = useMsal();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (!tokenManager.isAuthenticated()) {
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/auth/profile`, {
          headers: {
            ...tokenManager.getAuthHeader(),
            'Content-Type': 'application/json',
          },
        });
        if (response.status === 401) {
          tokenManager.clearAuth();
          setUser(null);
        } else if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          tokenManager.clearAuth();
          setUser(null);
        }
      } catch (error) {
        tokenManager.clearAuth();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await response.json();
      
      // Check if user has multiple roles
      if (data.user.hasMultipleRoles) {
        // Check if user has admin or itsupport roles
        const hasAdminRole = data.user.roles && 
          (data.user.roles.includes('admin') || data.user.roles.includes('itsupport'));
        
        if (hasAdminRole) {
          // Admin users see role selection dialog
          tokenManager.setToken(data.token);
          setPendingUser(data.user);
          setShowRoleSelection(true);
          return { success: true, user: data.user, requiresRoleSelection: true };
        } else {
          // Non-admin users automatically login as employee
          tokenManager.setToken(data.token);
          
          // Call backend to set primary role as employee
          try {
            const roleResponse = await fetch(`${API_URL}/auth/primary-role`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${data.token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ primaryRole: 'employee' }),
            });

            if (roleResponse.ok) {
              // eslint-disable-next-line no-unused-vars
              const roleData = await roleResponse.json();
              const updatedUser = {
                ...data.user,
                primaryRole: 'employee',
                role: 'employee'
              };
              setUser(updatedUser);
              return { success: true, user: updatedUser };
            }
          } catch (err) {
            console.error('Error setting primary role:', err);
          }
          
          // Fallback: set user even if role update fails
          const fallbackUser = {
            ...data.user,
            primaryRole: 'employee',
            role: 'employee'
          };
          setUser(fallbackUser);
          return { success: true, user: fallbackUser };
        }
      }

      // Store token only
      tokenManager.setToken(data.token);
      setUser(data.user); // Store user in React state only
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Microsoft login function
  const microsoftLogin = async () => {
    try {
      const response = await instance.loginPopup(loginRequest);
      const claims = response.idTokenClaims;

      // Set the account as active after successful login
      instance.setActiveAccount(response.account);

      // Fetch department information from Microsoft Graph API
      const departmentInfo = await fetchUserDepartment(instance);

      // Extract roles from Microsoft claims
      const microsoftRoles = claims.roles || claims.role ? 
        (Array.isArray(claims.roles) ? claims.roles : [claims.role]) : 
        ['employee'];

      const userInfo = {
        email: claims.email || claims.preferred_username,
        firstName: claims.given_name,
        lastName: claims.family_name,
        role: (claims.role || claims.roles?.[0] || 'employee').toLowerCase(),
        roles: microsoftRoles.map(r => r.toLowerCase()),
        department: departmentInfo.department,
        jobTitle: departmentInfo.jobTitle,
        officeLocation: departmentInfo.officeLocation,
      };

      // Send to backend with department information
      const backendResponse = await fetch(`${API_URL}/auth/login/microsoft`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userInfo),
      });

      const data = await backendResponse.json();

      if (!backendResponse.ok) {
        throw new Error(data.message || "Login failed");
      }

      // Check if user has multiple roles
      if (data.user.hasMultipleRoles) {
        // Check if user has admin or itsupport roles
        const hasAdminRole = data.user.roles && 
          (data.user.roles.includes('admin') || data.user.roles.includes('itsupport'));
        
        if (hasAdminRole) {
          // Admin users see role selection dialog
          tokenManager.setToken(data.token);
          setPendingUser(data.user);
          setShowRoleSelection(true);
          return { success: true, user: data.user, requiresRoleSelection: true };
        } else {
          // Non-admin users automatically login as employee
          tokenManager.setToken(data.token);
          
          // Call backend to set primary role as employee
          try {
            const roleResponse = await fetch(`${API_URL}/auth/primary-role`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${data.token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ primaryRole: 'employee' }),
            });

            if (roleResponse.ok) {
              // eslint-disable-next-line no-unused-vars
              const roleData = await roleResponse.json();
              const updatedUser = {
                ...data.user,
                primaryRole: 'employee',
                role: 'employee'
              };
              setUser(updatedUser);
              return { success: true, user: updatedUser };
            }
          } catch (err) {
            console.error('Error setting primary role:', err);
          }
          
          // Fallback: set user even if role update fails
          const fallbackUser = {
            ...data.user,
            primaryRole: 'employee',
            role: 'employee'
          };
          setUser(fallbackUser);
          return { success: true, user: fallbackUser };
        }
      }

      // Store token only
      tokenManager.setToken(data.token);
      setUser(data.user); // Store user in React state only
      return { success: true, user: data.user };
  } catch (error) {
    console.error("Microsoft login error:", error);
    
    // Handle specific hash empty error
    if (error.message && error.message.includes('hash_empty_error')) {
      throw new Error('Authentication failed. Please try refreshing the page and signing in again.');
    }
    
    // Handle other MSAL errors
    if (error.errorCode) {
      switch (error.errorCode) {
        case 'user_cancelled':
          throw new Error('Sign-in was cancelled. Please try again.');
        case 'popup_window_error':
          throw new Error('Popup window was blocked. Please allow popups and try again.');
        case 'network_error':
          throw new Error('Network error. Please check your connection and try again.');
        default:
          throw new Error('Authentication failed. Please try again.');
      }
    }
    
    throw error;
  }
  };

  // Role selection function
  const selectRole = async (selectedRole) => {
    try {
      if (!pendingUser) {
        throw new Error('No pending user found');
      }

      // Ensure we have a token before making the API call
      const token = tokenManager.getToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Call backend to update primary role
      const response = await fetch(`${API_URL}/auth/primary-role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ primaryRole: selectedRole }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update role');
      }

      // eslint-disable-next-line no-unused-vars
      const data = await response.json();

      // Update user with selected role
      const updatedUser = {
        ...pendingUser,
        primaryRole: selectedRole,
        role: selectedRole // For backward compatibility
      };

      // Store token and user
      setUser(updatedUser);
      setPendingUser(null);
      setShowRoleSelection(false);

      return { success: true, user: updatedUser };
    } catch (error) {
      console.error('Role selection error:', error);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout endpoint
              await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          ...tokenManager.getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // Clear localStorage regardless of API response
      tokenManager.clearAuth();
      setUser(null);
    }
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return user?.role === role;
  };

  // Get current user
  const getCurrentUser = () => {
    return user;
  };

  // Check if authenticated
  const isAuthenticated = () => {
    return !!user && tokenManager.isAuthenticated();
  };

  // Get auth headers for API calls
  const getAuthHeaders = () => {
    return {
      ...tokenManager.getAuthHeader(),
      'Content-Type': 'application/json',
    };
  };

  const value = {
    user,
    loading,
    login,
    microsoftLogin,
    logout,
    hasRole,
    getCurrentUser,
    isAuthenticated,
    getAuthHeaders,
    tokenManager,
    showRoleSelection,
    setShowRoleSelection,
    pendingUser,
    selectRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export tokenManager for direct use if needed
export { tokenManager }; 